import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { isEmpty } from 'lodash';

// Types
import { CheckActivationCodeValidityAndTrialMode, FailedCall } from '../../../shared/types/manageCalls';
import { ToFreeze } from '../../../shared/types/common';
import { StorageKey } from '../../../shared/types/storage';

// Http
import { ApiService } from '../../http/api.service';

// Services
import { CommonService } from '../../services/common/common.service';
import { LoggerService } from '../../services/logger/logger.service';
import { NetworkService } from '../../services/network/network.service';
import { StorageService } from '../../services/storage/storage.service';
import { ActivationCodeService } from '../../services/activation-code/activation-code.service';
import { PwaService } from '../../services/pwa/pwa.service';

export const CheckActivationCodeGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const HOME = '/home';
  const logger = inject(LoggerService);
  const common = inject(CommonService);
  const network = inject(NetworkService);
  const pwa = inject(PwaService);
  const api = inject(ApiService);
  const storage = inject(StorageService);
  const activationCodeService = inject(ActivationCodeService);

  const checkRoute = (url: string, stateUrl: string): void => {
    logger.consoleLog('CheckActivationCodeGuard', 'checkRoute', { url, stateUrl });

    if (activationCodeService.activationCode) {
      api.activationCode.checkActivationCodeValidity().subscribe({
        next: async (data: CheckActivationCodeValidityAndTrialMode) => {
          activationCodeService.activationCodeValidity = data;

          storage.getFreezedState('CheckActivationCodeGuard 66').subscribe({
            next: (lastState: ToFreeze) => {
              lastState.activationCodeValidity = { ...activationCodeService.activationCodeValidity };

              storage.setOnStorage(StorageKey.FreezeLastState, lastState).subscribe();
            },
          });

          const { canUseApp, expirationMessage } = data;
          if (!canUseApp && !activationCodeService.alertActivationCode && stateUrl !== HOME) {
            await activationCodeService.showErrorAndRemoveActivationCode(expirationMessage);
          }
        },
        error: async (failedCall: FailedCall) => {
          if (failedCall?.code === 'NO_VALID_ACTIVATION_CODE') {
            await activationCodeService.showErrorAndRemoveActivationCode(failedCall.error);
          } else {
            getLastFreezedState().then(() => {
              logger.consoleLog('CheckActivationCodeGuard', 'notConnectedCallback', 'appNotEnabled');

              if (activationCodeService.activationCodeValidity !== undefined) {
                if (!activationCodeService.activationCodeValidity.isLifeTimeActivationCode) {
                  const hasActivationCodeExpired = activationCodeService.activationCodeValidity.isUsageActivationCode
                    ? activationCodeService.activationCodeValidity.usageTime -
                        activationCodeService.activationCodeValidity.numberOfUsage <
                      0
                    : new Date(activationCodeService.activationCodeValidity.timeActivationCodeExpiration).getTime() <
                      Date.now();

                  logger.consoleLog('CheckActivationCodeGuard', 'activationCodeValidity', {
                    hasActivationCodeExpired,
                  });

                  if (hasActivationCodeExpired) {
                    void activationCodeService.showErrorAndRemoveActivationCode();
                  }
                }
              }
            });
          }
        },
      });
    }
  };

  const getLastFreezedState = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      logger.consoleLog('CheckActivationCodeGuard', 'getLastFreezedState');

      storage.getFreezedState('CheckActivationCodeGuard 108').subscribe({
        next: (freezed: ToFreeze) => {
          if (freezed !== null && !isEmpty(freezed)) {
            activationCodeService.activationCodeValidity = { ...freezed.activationCodeValidity };
          }
          resolve();
        },
      });
    });
  };

  return new Observable((observer) => {
    common.getPlatform();
    pwa.checkIfRunningInIncognito();

    const { url } = state;
    logger.consoleLog('CheckActivationCodeGuard', 'canActivate', {
      url,
      previousUrl: network.previousUrl,
    });

    storage.getFromStorage(StorageKey.ActivationCode).subscribe({
      next: (activationCode: string) => {
        logger.consoleLog('CheckActivationCodeGuard', 'activationCode', { activationCode });
        activationCodeService.activationCode = activationCode;

        observer.next(true);

        checkRoute(url, state.url);
      },
    });
  });
};
